/**
 * OperationalManualPage — Renders the dealer/seller manual served by the
 * FastAPI `/api/operational-manual` endpoint. Markdown is rendered with
 * react-markdown + remark-gfm so we get tables, task lists and footnotes.
 *
 * Features the panel ships with (Fabrizio + Mara 12/06/2026):
 *   - Sticky left-side Table of Contents (TOC) generated from H2/H3 headings
 *     with scroll-spy highlight of the section currently in view.
 *   - Language switcher (6 native UI languages: IT/UK/FR/ES/DE/PL).
 *     Backend on-demand translates from the Italian master via Claude
 *     Sonnet 4.5 (~6s the first time, instant from cache thereafter).
 *   - "Download PDF" button that streams the manual rendered as PDF.
 *   - Print-friendly layout (Ctrl+P from the in-app view also works).
 *
 * Mounted at `/dealer/manual` and `/seller/manual`. The `role` prop
 * decides which markdown source to fetch and which contact channels are
 * surfaced (e.g. "contact your dealer titolare" for sellers vs "contact
 * support@lojack.eu" for dealers).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Download, BookOpen, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// 18 SOC-country languages, aligned with backend `_SUPPORTED_LANGS` in
// routes/operational_manual.py and services/i18n.py OPERATION_CENTERS.
// Regional variants (BE/CH/GBZ/IRL) coexist with their base language for
// terminology consistency with the SOC directory.
const SUPPORTED_LANGS = [
  { code: "IT", flag: "🇮🇹", label: "Italiano" },
  { code: "UK", flag: "🇬🇧", label: "English" },
  { code: "FR", flag: "🇫🇷", label: "Français" },
  { code: "ES", flag: "🇪🇸", label: "Español" },
  { code: "DE", flag: "🇩🇪", label: "Deutsch" },
  { code: "PL", flag: "🇵🇱", label: "Polski" },
  { code: "PT", flag: "🇵🇹", label: "Português" },
  { code: "NL", flag: "🇳🇱", label: "Nederlands" },
  { code: "BE", flag: "🇧🇪", label: "Français (BE)" },
  { code: "SE", flag: "🇸🇪", label: "Svenska" },
  { code: "NO", flag: "🇳🇴", label: "Norsk" },
  { code: "FI", flag: "🇫🇮", label: "Suomi" },
  { code: "DK", flag: "🇩🇰", label: "Dansk" },
  { code: "CZ", flag: "🇨🇿", label: "Čeština" },
  { code: "GR", flag: "🇬🇷", label: "Ελληνικά" },
  { code: "CH", flag: "🇨🇭", label: "Deutsch (CH)" },
  { code: "GBZ", flag: "🇬🇮", label: "English (GBZ)" },
  { code: "IRL", flag: "🇮🇪", label: "English (IRL)" },
];

const slugify = (text) =>
  String(text).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-");

// react-markdown component overrides — defined OUTSIDE the page component
// so React doesn't tear down the subtree on every parent re-render
// (react/no-unstable-nested-components).
const MD_COMPONENTS = {
  h1: ({ node, children, ...props }) => (
    <h1 id={slugify(String(children))} {...props}>{children}</h1>
  ),
  h2: ({ node, children, ...props }) => (
    <h2 id={slugify(String(children))} {...props}>{children}</h2>
  ),
  h3: ({ node, children, ...props }) => (
    <h3 id={slugify(String(children))} {...props}>{children}</h3>
  ),
  img: ({ node, ...props }) => (
    <img
      {...props}
      className="rounded-lg border border-gray-800 bg-white p-1 max-w-full"
      alt={props.alt || ""}
    />
  ),
};

export default function OperationalManualPage({ role = "dealer", backLink = "/dealer" }) {
  const [lang, setLang] = useState(() => {
    // Fallback chain: sticky manual choice → dealer portal language
    // (synced with the dealer's country) → IT.
    try {
      return localStorage.getItem(`lojack-manual-lang:${role}`)
        || localStorage.getItem("dealer_lang")
        || "IT";
    }
    catch (_) { return "IT"; }
  });
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const fetchManual = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/operational-manual/${role}.md`, {
          // `_t` is a cache-buster query param: forces axios + CDN + browser
          // to fetch fresh markdown instead of replaying a stale 404 from a
          // race window during the initial deploy.
          params: { lang, _t: Date.now() },
          signal: controller.signal,
          headers: { "Cache-Control": "no-cache" },
          // Mara CR 20/02/2026 — cold-path translation (Claude Sonnet 4.5
          // first call for a given lang) can take 10-30s. Default axios
          // timeout would surface as "Network Error" via the Cloudflare
          // 504. Bumped to 90s + the backend now eagerly warms the cache
          // at startup so this delay only affects the very first dealer
          // who lands while the warmup task is still running.
          timeout: 90000,
        });
        if (!controller.signal.aborted) setMarkdown(res.data);
      } catch (err) {
        if (!controller.signal.aborted && err?.name !== "CanceledError") {
          const status = err?.response?.status;
          let msg;
          if (status === 404) {
            msg = "Manuale non trovato sul server (HTTP 404). Probabile cache vecchia.";
          } else if (status === 502 || status === 503 || status === 504 || err?.code === "ECONNABORTED") {
            // Cold path + proxy timeout. Suggest one retry — the
            // background warmup may have finished by the next click.
            msg = "Il manuale è in fase di preparazione (prima richiesta in questa lingua). Riprova fra qualche secondo.";
          } else {
            msg = err?.message || "Errore caricamento manuale";
          }
          setError(msg);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchManual();
    try { localStorage.setItem(`lojack-manual-lang:${role}`, lang); } catch (_) { /* ignore quota errors */ }
    return () => { controller.abort(); };
  }, [role, lang]);

  // Build the Table of Contents from H2/H3 headings detected in the markdown.
  // Cheap parser: regex over lines, since we don't need full AST awareness.
  const toc = useMemo(() => {
    const out = [];
    markdown.split("\n").forEach((line) => {
      const m2 = /^##\s+(.+?)\s*$/.exec(line);
      const m3 = /^###\s+(.+?)\s*$/.exec(line);
      if (m2) out.push({ level: 2, text: m2[1], id: slugify(m2[1]) });
      else if (m3) out.push({ level: 3, text: m3[1], id: slugify(m3[1]) });
    });
    return out;
  }, [markdown]);

  // Scroll-spy: highlight the TOC entry of the section currently in view.
  useEffect(() => {
    if (!contentRef.current) return;
    const headings = contentRef.current.querySelectorAll("h2, h3");
    if (!headings.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => a.target.offsetTop - b.target.offsetTop);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: 0.1 }
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [markdown]);

  const pdfHref = `${BACKEND_URL}/api/operational-manual/${role}.pdf?lang=${lang}`;
  const title = role === "dealer"
    ? "Manuale Operativo Dealer" : "Manuale Operativo Venditore";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-100">
      {/* Header */}
      <div className="border-b border-gray-800 bg-black/40 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Link
              to={backLink}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
              data-testid="manual-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Torna alla dashboard</span>
            </Link>
            <div className="h-6 w-px bg-gray-800" />
            <div className="flex items-center gap-2 text-white">
              <BookOpen className="w-5 h-5 text-lojack-clause-400" />
              <h1 className="text-lg sm:text-xl font-semibold">{title}</h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="bg-gray-800 border border-gray-700 text-sm rounded-lg px-3 py-1.5 text-gray-100"
              data-testid="manual-lang-select"
              aria-label="Lingua manuale"
            >
              {SUPPORTED_LANGS.map((l) => (
                <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
              ))}
            </select>
            <a
              href={pdfHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-lojack-clause-500 hover:bg-lojack-clause-600 text-white text-sm rounded-lg px-4 py-1.5 transition-colors"
              data-testid="manual-pdf-btn"
            >
              <Download className="w-4 h-4" />
              <span>PDF</span>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-8">
        {/* TOC (sticky on desktop) */}
        <aside
          className="hidden lg:block sticky top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto"
          data-testid="manual-toc"
        >
          <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-3">Indice</h2>
          <nav className="space-y-1 text-sm">
            {toc.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={`block transition-colors py-1 ${
                  item.level === 3 ? "pl-4 text-xs" : "pl-1"
                } ${
                  activeId === item.id
                    ? "text-lojack-clause-400 font-medium"
                    : "text-gray-400 hover:text-gray-200"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(item.id);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  setActiveId(item.id);
                }}
              >
                {item.text}
              </a>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <article
          ref={contentRef}
          className="prose prose-invert prose-headings:text-white prose-h1:text-lojack-clause-300
                     prose-h2:text-lojack-clause-300 prose-h2:border-b prose-h2:border-gray-800
                     prose-h2:pb-2 prose-h3:text-white prose-a:text-lojack-clause-400
                     prose-strong:text-white prose-code:bg-gray-800 prose-code:px-1
                     prose-code:rounded prose-blockquote:border-l-lojack-clause-500
                     prose-blockquote:text-gray-300 prose-table:text-sm max-w-none"
          data-testid="manual-content"
        >
          {loading ? (
            <div className="flex items-center gap-3 text-gray-400 py-12">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Caricamento manuale ({lang})...</span>
            </div>
          ) : error ? (
            <div className="py-12 flex flex-col items-start gap-4" data-testid="manual-error-state">
              <div className="text-red-400 text-base">⚠️ {error}</div>
              <div className="text-sm text-gray-400 max-w-prose">
                Se il problema persiste, prova un <strong>hard refresh</strong> della pagina:
                premi <kbd className="bg-gray-800 px-1.5 py-0.5 rounded text-xs">Ctrl+Shift+R</kbd>
                {" "}su Windows/Linux o <kbd className="bg-gray-800 px-1.5 py-0.5 rounded text-xs">Cmd+Shift+R</kbd>
                {" "}su Mac per svuotare la cache del browser.
              </div>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="text-sm bg-lojack-clause-500 hover:bg-lojack-clause-600 text-white rounded-lg px-4 py-2 transition-colors"
                data-testid="manual-retry-btn"
              >
                Riprova
              </button>
            </div>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={MD_COMPONENTS}
            >
              {markdown}
            </ReactMarkdown>
          )}
        </article>
      </div>
    </div>
  );
}
